import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiChat, type AIConversation, type AIMessage } from '@/lib/api';
import {
  Send,
  Plus,
  Loader2,
  PanelLeftClose,
  PanelLeft,
  ChevronDown,
  User,
  Bot,
  Trash2,
  Pin,
  PinOff,
  Archive,
  ArchiveRestore,
  Search,
  RefreshCw,
  Copy,
  Check,
  FolderPlus,
  Folder,
  MessageSquare,
  Download,
  Sparkles,
  Lock,
} from 'lucide-react';

// ─── Model definitions ──────────────────────────────────────────────────────

type ModelId = 'gpt-4.1' | 'gpt-4.1-mini' | 'gpt-4.1-nano' | 'gpt-4o' | 'claude-sonnet-4-6' | 'claude-haiku-4-5' | 'claude-opus-4-6' | 'auto';

interface ModelDef {
  id: ModelId;
  label: string;
  description: string;
  tier: 'free' | 'basic' | 'pro' | 'enterprise';
}

const MODELS: ModelDef[] = [
  { id: 'auto', label: 'Auto', description: 'Smart model selection', tier: 'free' },
  { id: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', description: 'Fast & affordable', tier: 'free' },
  { id: 'gpt-4.1-nano', label: 'GPT-4.1 Nano', description: 'Fastest responses', tier: 'free' },
  { id: 'gpt-4.1', label: 'GPT-4.1', description: 'Most capable GPT', tier: 'basic' },
  { id: 'gpt-4o', label: 'GPT-4o', description: 'Best for code & vision', tier: 'basic' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', description: 'Balanced reasoning', tier: 'pro' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', description: 'Quick & smart', tier: 'pro' },
  { id: 'claude-opus-4-6', label: 'Claude Opus 4.6', description: 'Deep analysis', tier: 'enterprise' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ─── Component ───────────────────────────────────────────────────────────────

export function TheOneAI() {
  const queryClient = useQueryClient();

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ convId: string; x: number; y: number } | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelId>('gpt-4.1-mini');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // ─── Queries ────────────────────────────────────────────────────────────────

  const { data: conversationsData } = useQuery({
    queryKey: ['ai-conversations', showArchived],
    queryFn: () => aiChat.conversations.list({ archived: showArchived }),
    refetchInterval: 30_000,
  });
  const conversations = conversationsData?.conversations ?? [];

  const { data: foldersData } = useQuery({
    queryKey: ['ai-folders'],
    queryFn: () => aiChat.folders.list(),
  });
  const folders = foldersData?.folders ?? [];

  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: ['ai-messages', activeConversationId],
    queryFn: () => activeConversationId ? aiChat.messages.list(activeConversationId) : Promise.resolve({ messages: [] }),
    enabled: !!activeConversationId,
  });
  const messages = messagesData?.messages ?? [];

  const { data: searchData } = useQuery({
    queryKey: ['ai-search', searchQuery],
    queryFn: () => aiChat.conversations.search(searchQuery),
    enabled: searchQuery.length >= 2,
  });

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const sendMessage = useMutation({
    mutationFn: (body: { conversationId: string; content: string; model_id?: string }) =>
      aiChat.messages.send(body.conversationId, { content: body.content, model_id: body.model_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-messages', activeConversationId] });
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
  });

  const regenerateMessage = useMutation({
    mutationFn: (conversationId: string) => aiChat.messages.regenerate(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-messages', activeConversationId] });
    },
  });

  const updateConversation = useMutation({
    mutationFn: (args: { id: string; body: Partial<Pick<AIConversation, 'title' | 'pinned' | 'archived' | 'model_id' | 'folder_id'>> }) =>
      aiChat.conversations.update(args.id, args.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
  });

  const deleteConversation = useMutation({
    mutationFn: (id: string) => aiChat.conversations.delete(id),
    onSuccess: (_, id) => {
      if (activeConversationId === id) setActiveConversationId(null);
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
  });

  const createFolder = useMutation({
    mutationFn: (name: string) => aiChat.folders.create(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-folders'] }),
  });

  // ─── Effects ────────────────────────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isStreaming, scrollToBottom]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 24 * 6;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [inputValue]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
        setModelDropdownOpen(false);
      }
      if (contextMenu) setContextMenu(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming) return;

    setInputValue('');
    setIsStreaming(true);

    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      let convId = activeConversationId;

      // Create new conversation if none active
      if (!convId) {
        const conv = await aiChat.conversations.create({ model_id: selectedModel === 'auto' ? 'gpt-4.1-mini' : selectedModel });
        convId = conv.id;
        setActiveConversationId(convId);
        queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
      }

      await sendMessage.mutateAsync({
        conversationId: convId,
        content: trimmed,
        model_id: selectedModel === 'auto' ? undefined : selectedModel,
      });
    } catch (err) {
      // Error handled by mutation
    } finally {
      setIsStreaming(false);
      textareaRef.current?.focus();
    }
  }, [inputValue, isStreaming, activeConversationId, selectedModel, sendMessage, queryClient]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setActiveConversationId(null);
    setInputValue('');
    textareaRef.current?.focus();
  };

  const handleCopy = async (content: string, msgId: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(msgId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExport = async () => {
    if (!activeConversationId) return;
    const conv = conversations.find((c) => c.id === activeConversationId);
    const md = messages.map((m) => `**${m.role === 'user' ? 'You' : 'AI'}** (${new Date(m.created_at).toLocaleString()}):\n${m.content}`).join('\n\n---\n\n');
    const blob = new Blob([`# ${conv?.title ?? 'Chat'}\n\n${md}`], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(conv?.title ?? 'chat').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Active conversation info ───────────────────────────────────────────────

  const activeConv = conversations.find((c) => c.id === activeConversationId);
  const currentModel = MODELS.find((m) => m.id === selectedModel) ?? { id: 'gpt-4.1-mini' as ModelId, label: 'GPT-4.1 Mini', description: 'Fast & affordable', tier: 'free' as const };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full overflow-hidden">
      {/* ─── Sidebar ───────────────────────────────────────────────────── */}
      {sidebarOpen && (
        <aside ref={sidebarRef} className="w-72 flex-shrink-0 border-r border-border bg-card flex flex-col overflow-hidden">
          {/* Sidebar header */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            <button
              onClick={handleNewChat}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Chat
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-1.5 rounded hover:bg-accent text-muted-foreground"
                title="Search"
              >
                <Search className="h-4 w-4" />
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 rounded hover:bg-accent text-muted-foreground"
                title="Close sidebar"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <div className="p-2 border-b border-border">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="w-full px-3 py-1.5 rounded-md border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
                autoFocus
              />
              {searchData && searchQuery.length >= 2 && (
                <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                  {searchData.conversations.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setActiveConversationId(c.id); setSearchOpen(false); setSearchQuery(''); }}
                      className="w-full text-left px-2 py-1.5 rounded text-sm text-foreground hover:bg-accent truncate"
                    >
                      {c.title}
                    </button>
                  ))}
                  {searchData.conversations.length === 0 && searchData.messages.length === 0 && (
                    <p className="text-xs text-muted-foreground px-2 py-1">No results</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {/* Pinned conversations */}
            {conversations.filter((c) => c.pinned).length > 0 && (
              <div className="px-2 pt-2">
                <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Pinned</p>
                {conversations.filter((c) => c.pinned).map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    conv={conv}
                    active={conv.id === activeConversationId}
                    onClick={() => setActiveConversationId(conv.id)}
                    onContextMenu={(e) => { e.preventDefault(); setContextMenu({ convId: conv.id, x: e.clientX, y: e.clientY }); }}
                  />
                ))}
              </div>
            )}

            {/* Folder sections */}
            {folders.map((folder) => {
              const folderConvos = conversations.filter((c) => c.folder_id === folder.id && !c.pinned);
              if (folderConvos.length === 0) return null;
              return (
                <div key={folder.id} className="px-2 pt-2">
                  <p className="text-xs font-medium text-muted-foreground px-2 mb-1 flex items-center gap-1">
                    <Folder className="h-3 w-3" />
                    {folder.name}
                  </p>
                  {folderConvos.map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      conv={conv}
                      active={conv.id === activeConversationId}
                      onClick={() => setActiveConversationId(conv.id)}
                      onContextMenu={(e) => { e.preventDefault(); setContextMenu({ convId: conv.id, x: e.clientX, y: e.clientY }); }}
                    />
                  ))}
                </div>
              );
            })}

            {/* Unfiled conversations */}
            <div className="px-2 pt-2 pb-2">
              {conversations.filter((c) => !c.pinned && !c.folder_id).length > 0 && (
                <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Recent</p>
              )}
              {conversations.filter((c) => !c.pinned && !c.folder_id).map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conv={conv}
                  active={conv.id === activeConversationId}
                  onClick={() => setActiveConversationId(conv.id)}
                  onContextMenu={(e) => { e.preventDefault(); setContextMenu({ convId: conv.id, x: e.clientX, y: e.clientY }); }}
                />
              ))}
            </div>
          </div>

          {/* Sidebar footer */}
          <div className="p-2 border-t border-border flex items-center justify-between">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Archive className="h-3 w-3" />
              {showArchived ? 'Show Active' : 'Archived'}
            </button>
            <button
              onClick={() => {
                const name = prompt('Folder name:');
                if (name) createFolder.mutate(name);
              }}
              className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <FolderPlus className="h-3 w-3" />
              Folder
            </button>
          </div>
        </aside>
      )}

      {/* ─── Context Menu ──────────────────────────────────────────────── */}
      {contextMenu && (
        <div
          className="fixed z-50 w-48 rounded-md border border-border bg-card shadow-lg py-1"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {(() => {
            const conv = conversations.find((c) => c.id === contextMenu.convId);
            if (!conv) return null;
            return (
              <>
                <button
                  className="w-full text-left px-3 py-1.5 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                  onClick={() => { updateConversation.mutate({ id: conv.id, body: { pinned: !conv.pinned } }); setContextMenu(null); }}
                >
                  {conv.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                  {conv.pinned ? 'Unpin' : 'Pin'}
                </button>
                <button
                  className="w-full text-left px-3 py-1.5 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                  onClick={() => { updateConversation.mutate({ id: conv.id, body: { archived: !conv.archived } }); setContextMenu(null); }}
                >
                  {conv.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                  {conv.archived ? 'Unarchive' : 'Archive'}
                </button>
                <button
                  className="w-full text-left px-3 py-1.5 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                  onClick={() => {
                    const title = prompt('New title:', conv.title);
                    if (title) updateConversation.mutate({ id: conv.id, body: { title } });
                    setContextMenu(null);
                  }}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  Rename
                </button>
                <div className="h-px bg-border my-1" />
                <button
                  className="w-full text-left px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
                  onClick={() => {
                    if (confirm('Delete this conversation?')) {
                      deleteConversation.mutate(conv.id);
                    }
                    setContextMenu(null);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* ─── Main Chat Area ────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-card">
          {!sidebarOpen && (
            <button onClick={() => setSidebarOpen(true)} className="p-1 rounded hover:bg-accent text-muted-foreground" title="Open sidebar">
              <PanelLeft className="h-4 w-4" />
            </button>
          )}

          {/* Model selector */}
          <div className="relative" ref={modelDropdownRef}>
            <button
              type="button"
              onClick={() => setModelDropdownOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-background text-sm text-foreground hover:border-primary/50 transition-colors"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span>{currentModel.label}</span>
              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${modelDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {modelDropdownOpen && (
              <div className="absolute z-50 mt-1 w-64 rounded-md border border-border bg-card shadow-lg py-1">
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { setSelectedModel(m.id); setModelDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center justify-between ${
                      m.id === selectedModel ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-1.5">
                        {m.label}
                        {m.tier === 'enterprise' && <Lock className="h-3 w-3 text-muted-foreground" />}
                      </div>
                      <div className="text-xs text-muted-foreground">{m.description}</div>
                    </div>
                    {m.id === selectedModel && <Check className="h-4 w-4 text-primary" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {activeConv && (
              <h1 className="text-sm font-medium text-foreground truncate">{activeConv.title}</h1>
            )}
          </div>

          {/* Header actions */}
          <div className="flex items-center gap-1">
            {activeConversationId && (
              <>
                <button
                  onClick={handleExport}
                  className="p-1.5 rounded hover:bg-accent text-muted-foreground"
                  title="Export as Markdown"
                >
                  <Download className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          {!activeConversationId && messages.length === 0 ? (
            <EmptyState onSelectPrompt={(prompt) => { setInputValue(prompt); textareaRef.current?.focus(); }} />
          ) : messagesLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  copiedId={copiedId}
                  onCopy={(content) => handleCopy(content, msg.id)}
                  onRegenerate={msg.role === 'assistant' && msg.id === messages[messages.length - 1]?.id
                    ? () => activeConversationId && regenerateMessage.mutate(activeConversationId)
                    : undefined
                  }
                />
              ))}
              {(isStreaming || sendMessage.isPending || regenerateMessage.isPending) && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-border bg-card px-4 py-3">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Message The One AI..."
                  rows={1}
                  disabled={isStreaming || sendMessage.isPending}
                  className="w-full border border-border rounded-lg px-4 py-2.5 bg-background text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder:text-muted-foreground disabled:opacity-50"
                  style={{ minHeight: '42px' }}
                />
              </div>
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isStreaming || sendMessage.isPending}
                className="flex-shrink-0 h-[42px] w-[42px] flex items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Send message"
              >
                {isStreaming || sendMessage.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 text-center">
              {currentModel.label} {selectedModel !== 'auto' && '|'} Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ConversationItem({
  conv,
  active,
  onClick,
  onContextMenu,
}: {
  conv: AIConversation;
  active: boolean;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors group ${
        active
          ? 'bg-primary/10 text-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      }`}
    >
      <div className="flex items-center gap-2">
        {conv.pinned && <Pin className="h-3 w-3 text-primary flex-shrink-0" />}
        <span className="truncate flex-1">{conv.title}</span>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-xs text-muted-foreground">{timeAgo(conv.updated_at)}</span>
        {conv.message_count > 0 && (
          <span className="text-xs text-muted-foreground">{conv.message_count} msgs</span>
        )}
      </div>
    </button>
  );
}

function MessageBubble({
  message,
  copiedId,
  onCopy,
  onRegenerate,
}: {
  message: AIMessage;
  copiedId: string | null;
  onCopy: (content: string) => void;
  onRegenerate?: () => void;
}) {
  const isUser = message.role === 'user';
  const isError = !isUser && message.content.startsWith('[Error]');

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${isUser ? 'bg-primary/10' : 'bg-muted'}`}>
        {isUser ? <User className="h-4 w-4 text-primary" /> : <Bot className="h-4 w-4 text-muted-foreground" />}
      </div>

      <div className={`max-w-[75%] group`}>
        <div
          className={`rounded-lg px-4 py-2.5 text-sm ${
            isUser
              ? 'bg-primary text-primary-foreground'
              : isError
                ? 'bg-destructive/10 text-destructive border border-destructive/20'
                : 'bg-muted text-foreground'
          }`}
        >
          <p className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
        </div>

        {/* Message meta + actions */}
        <div className={`flex items-center gap-2 mt-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
          {message.model_id && !isUser && (
            <span className="text-xs text-muted-foreground">{message.model_id}</span>
          )}
          <span className="text-xs text-muted-foreground">
            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>

          {!isUser && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
              <button onClick={() => onCopy(message.content)} className="p-1 rounded hover:bg-accent text-muted-foreground" title="Copy">
                {copiedId === message.id ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
              </button>
              {onRegenerate && (
                <button onClick={onRegenerate} className="p-1 rounded hover:bg-accent text-muted-foreground" title="Regenerate">
                  <RefreshCw className="h-3 w-3" />
                </button>
              )}
            </div>
          )}

          {message.cost_usd !== undefined && message.cost_usd > 0 && !isUser && (
            <span className="text-xs text-muted-foreground opacity-60">${message.cost_usd.toFixed(4)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-muted flex items-center justify-center">
        <Bot className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="bg-muted rounded-lg px-4 py-3 flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

const STARTER_PROMPTS = [
  'Draft a ticket response for a network outage',
  'Create a QBR agenda for a 50-endpoint client',
  'Write a PowerShell script to audit M365 licenses',
  'Explain Zero Trust architecture for my clients',
  'Generate a phishing awareness email template',
  'Build an SOP for onboarding a new client',
];

function EmptyState({ onSelectPrompt }: { onSelectPrompt: (prompt: string) => void }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4">
      <div className="h-20 w-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
        <Sparkles className="h-10 w-10 text-primary" />
      </div>
      <h2 className="text-2xl font-semibold text-foreground mb-2">The One AI</h2>
      <p className="text-muted-foreground max-w-md mb-8">
        Your AI-powered assistant for MSP operations. Ask anything about IT management, client support, automation, and business strategy.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl w-full">
        {STARTER_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            className="text-left p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 text-sm text-muted-foreground transition-colors"
            onClick={() => onSelectPrompt(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}
